import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, User, BookOpen } from "lucide-react";
import { Button } from "@tabipla/ui/components/button";
import { LoadingState, EmptyState } from "@tabipla/ui";
import { MekkeLayout } from "../layouts/MekkeLayout";
import { PublicBookletCard } from "../components/discover/PublicBookletCard";
import { FollowButton } from "../components/users/FollowButton";
import { useUserProfile, useUserBooklets } from "../../application/hooks/useUserProfile";

export function UserProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: profile, isLoading: profileLoading } = useUserProfile(userId);
  const { data: booklets, isLoading: bookletsLoading } = useUserBooklets(userId);

  const isLoading = profileLoading || bookletsLoading;

  if (isLoading) {
    return (
      <MekkeLayout hideNav>
        <div className="min-h-screen pb-6">
          <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("common.back")}
              </Button>
            </div>
          </header>
          <LoadingState message={t("common.loading")} />
        </div>
      </MekkeLayout>
    );
  }

  if (!profile) {
    return (
      <MekkeLayout hideNav>
        <div className="min-h-screen pb-6">
          <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("common.back")}
              </Button>
            </div>
          </header>
          <div className="flex h-64 items-center justify-center">
            <p className="text-muted-foreground">User not found</p>
          </div>
        </div>
      </MekkeLayout>
    );
  }

  return (
    <MekkeLayout hideNav>
      <div className="min-h-screen pb-6">
        {/* Header */}
        <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("common.back")}
            </Button>
          </div>
        </header>

        <div className="mx-auto max-w-4xl">
          {/* Profile Header */}
          <div className="border-b bg-muted/30 px-4 py-6">
            <div className="flex items-start gap-4">
              {/* Avatar */}
              {profile.image ? (
                <img
                  src={profile.image}
                  alt={profile.name}
                  className="h-20 w-20 shrink-0 rounded-full object-cover"
                />
              ) : profile.avatarEmoji && profile.avatarColor ? (
                <div
                  className={`flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${profile.avatarColor} text-3xl`}
                >
                  {profile.avatarEmoji}
                </div>
              ) : (
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-muted">
                  <User className="h-10 w-10 text-muted-foreground" />
                </div>
              )}

              {/* Info */}
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-4">
                  <h1 className="truncate text-xl font-bold">{profile.name}</h1>
                  <FollowButton userId={profile.id} isFollowing={profile.isFollowing} />
                </div>

                {/* Stats */}
                <div className="mt-3 flex gap-6 text-sm">
                  <div>
                    <span className="font-semibold">{profile.bookletCount}</span>
                    <span className="ml-1 text-muted-foreground">{t("users.booklets")}</span>
                  </div>
                  <button
                    onClick={() => navigate(`/users/${profile.id}/followers`)}
                    className="transition-opacity hover:opacity-70"
                  >
                    <span className="font-semibold">{profile.followerCount}</span>
                    <span className="ml-1 text-muted-foreground">{t("users.followers")}</span>
                  </button>
                  <button
                    onClick={() => navigate(`/users/${profile.id}/following`)}
                    className="transition-opacity hover:opacity-70"
                  >
                    <span className="font-semibold">{profile.followingCount}</span>
                    <span className="ml-1 text-muted-foreground">{t("users.following")}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Booklets */}
          <div className="px-4 py-4">
            {booklets && booklets.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {booklets.map((booklet) => (
                  <PublicBookletCard key={booklet.id} booklet={booklet} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={BookOpen}
                title={t("users.noBooklets")}
                description={t("users.noBooklets")}
                variant="dashed"
              />
            )}
          </div>
        </div>
      </div>
    </MekkeLayout>
  );
}
